// src/components/hub/work/hooks/useWorkUndo.js
import { useCallback, useEffect, useRef, useState } from "react";
import { restoreWork } from "../../../../api/hub/work";

const UNDO_TOAST_MS = 10000;

// Owns the undo-toast lifecycle for the work page: state, the 10-second
// auto-dismiss timer (held in a ref so it's cleared on unmount and whenever a
// new toast replaces the current one — the old scattered setTimeout calls in
// WorkPage were never cleaned up), and the restore action (fn_work_restore).
const useWorkUndo = ({ activeBucket, setBucketCache, setRetryNonce, toastError }) => {
  const [undoToast, setUndoToast] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clear the pending timer on unmount (and if clearTimer identity ever changes).
  useEffect(() => clearTimer, [clearTimer]);

  const showUndoToast = useCallback((ids) => {
    clearTimer();
    setUndoToast({ ids, visible: true });
    timerRef.current = setTimeout(() => {
      setUndoToast(null);
      timerRef.current = null;
    }, UNDO_TOAST_MS);
  }, [clearTimer]);

  // UndoToast → fn_work_restore. On success clears the toast, evicts the
  // active bucket's cache entries (same startsWith pattern as before), and
  // bumps retryNonce so the bucket refetches.
  const handleRestore = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => restoreWork(id)));
      clearTimer();
      setUndoToast(null);
      setBucketCache((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => { if (key.startsWith(activeBucket)) delete next[key]; });
        return next;
      });
      setRetryNonce((n) => n + 1);
    } catch (e) { toastError(e?.message || "Restore failed", null, "restore"); }
  }, [activeBucket, clearTimer, setBucketCache, setRetryNonce, toastError]);

  return { undoToast, showUndoToast, handleRestore };
};

export default useWorkUndo;
